import { CurrentRecordModel } from '@records/state/current-record/current-record.state';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';
import { EntityType, IdentifyAccessForUsages } from '@miq/domain';
import { CURRENT_RECORD_STATE_TOKEN } from '../current-record/current-record-state.token';
import { ExecutableAction, ExecutableActionParam } from '../utils/executable-action';
import { CurrentDataModel } from './current-data.state';
import { processCompanyContacts } from './utils/preprocessor-company-contacts-factory';
import { ExtractUsagesInfoAndRecord } from './utils/process-usages';

export abstract class RecordFetcher {
  abstract fetch(...params: any): Observable<Record<string, any>>;
}

export class GetRecord implements ExecutableAction<CurrentDataModel> {
  static readonly type = '[Current Data] Get Record';

  /**
   *
   * @param setRecordLoad set to false when there is a secondary process
   *
   * This action loads and separates the record into the state, in the case that the setRecordLoad flag is true,
   * the action will turn the recordLoaded flag to true when successfully gets the record, and,
   *  in the case where the setRecordLoad is false, the action will not turn the recordLoaded when finishing.
   */
  constructor(private recordFetcher: RecordFetcher) {}

  execute(params: ExecutableActionParam<CurrentDataModel>) {
    params.ctx.patchState({ recordLoaded: false });
    const currentRecordState: CurrentRecordModel = params.store.selectSnapshot(CURRENT_RECORD_STATE_TOKEN);
    const entity = currentRecordState.type as EntityType;

    // fetch record
    let nextState$ = this._getRecord(params, entity);

    // also fetch associated property for availability, lease comp and sale comp
    if ([EntityType.Availability, EntityType.LeaseComp].includes(entity)) {
      nextState$ = nextState$.pipe(
        switchMap(nextState => {
          const { record } = nextState;
          // check record.property_id in case of cloning stage
          const propertyId = record?.property?.id ?? record?.property_id;
          if (propertyId == null) return of(nextState);

          return this.recordFetcher.fetch({ id: propertyId, type: EntityType.Property }).pipe(
            map(associatedProperty => ({
              ...nextState,
              associatedRecords: [{ type: EntityType.Property, data: associatedProperty }],
            }))
          );
        })
      );
    }

    return nextState$.pipe(
      tap(nextState => params.ctx.setState(nextState)),
      catchError(err => {
        params.ctx.setState({ record: null, usagesInfo: null, recordLoaded: false });
        return throwError(() => err);
      })
    );
  }

  private _getRecord(
    params: ExecutableActionParam<CurrentDataModel>,
    entityType: EntityType
  ): Observable<CurrentDataModel> {
    const processUsages = ExtractUsagesInfoAndRecord(IdentifyAccessForUsages(localStorage));
    return this.recordFetcher.fetch().pipe(
      map(record => {
        if (record) {
          const partialState = processUsages(record);
          record = processCompanyContacts(entityType, record);
          return { ...partialState, record, recordLoaded: true };
        }
        return { record: null, usagesInfo: null, recordLoaded: false };
      }),
      catchError(err => {
        params.ctx.setState({
          record: null,
          usagesInfo: null,
          recordLoaded: false,
        });
        return throwError(err);
      })
    );
  }
}
